import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Save, Edit2, Upload, FileSpreadsheet, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { listPricing, upsertPricing, deletePricing, uploadPricingExcel, getExchangeRate } from "@/lib/api";

const empty = { id: null, name_tr: "", name_en: "", name_ar: "", name_ru: "", price_eur: 0, region: "", note: "" };

export default function PricingTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [rate, setRate] = useState(null);
  const [rateUpdatedAt, setRateUpdatedAt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [replace, setReplace] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      setItems(await listPricing());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fiyat listesi yüklenemedi.");
      setItems([]);
    }
  };
  const refreshRate = async () => {
    try {
      const r = await getExchangeRate();
      setRate(r.eur_to_try);
      setRateUpdatedAt(r.fetched_at);
    } catch (_) {}
  };
  useEffect(() => { load(); refreshRate(); }, []);

  const save = async () => {
    if (!form.name_tr) return toast.error("Türkçe ad gerekli");
    try {
      await upsertPricing(form);
      toast.success(form.id ? "Güncellendi" : "Eklendi");
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi. Lütfen tekrar deneyin.");
    }
  };

  const edit = (p) => {
    setForm({ ...empty, ...p });
    // Scroll to form so user can see fields populated
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const del = async (id) => {
    try {
      await deletePricing(id);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi.");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadPricingExcel(file, replace);
      toast.success(`${r.inserted} satır eklendi (${r.skipped} atlandı)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const tryFor = (eur) => rate ? Math.round(eur * rate / 50) * 50 : null;
  const cashEur = (eur) => Math.round(eur * 0.9);

  return (
    <div className="space-y-6">
      {/* Excel upload */}
      <Card data-testid="excel-upload-card" className="border-primary/30">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Excel ile Toplu Fiyat Yükleme</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            <strong>3 sütun (esnek):</strong>{" "}
            <code className="bg-secondary px-1.5 py-0.5 rounded">TEDAVİ</code>,{" "}
            <code className="bg-secondary px-1.5 py-0.5 rounded">EURO</code>,{" "}
            <code className="bg-secondary px-1.5 py-0.5 rounded">TL</code>.
            Her satır için EURO <em>veya</em> TL'den hangisi varsa yazın — diğeri boş kalabilir.
            EURO ise sistem günlük kurla TL'ye çevirir; TL ise olduğu gibi kullanılır.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-xl">
            <div>
              <div className="text-sm font-medium">Mevcut listeyi sil ve değiştir</div>
              <div className="text-xs text-muted-foreground">Açıkken: tüm eski fiyatlar silinir, sadece Excel'dekiler kalır</div>
            </div>
            <Switch data-testid="replace-toggle" checked={replace} onCheckedChange={setReplace} />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={onUpload}
            data-testid="excel-input"
            className="hidden"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="excel-upload-btn"
            className="w-full h-12 rounded-full"
            style={{ background: "#8B9B88" }}
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Yükleniyor..." : "Excel Dosyası Seç ve Yükle"}
          </Button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/pricing/template.xlsx`}
            download
            data-testid="pricing-template-btn"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-1"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            Hazır şablonu indir (örnek satırlarla)
          </a>
          {replace && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>"Mevcut listeyi sil ve değiştir" açık. Yükleme yaptığınızda eski fiyatlar silinecek.</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exchange rate banner */}
      <Card className="border-primary/30">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-display">Güncel Döviz Kuru (1 Euro karşılığı Türk Lirası)</div>
            <div className="font-serif text-3xl mt-1" data-testid="exchange-rate-value">
              1 EUR = <span className="font-bold" style={{ color: "#B39B7A" }}>{rate ? rate.toFixed(2) : "—"}</span> TL
            </div>
            <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-2 flex-wrap">
              <span>Kaynak: Frankfurter (ECB referans kuru) • Her gün 06:00'da otomatik yenilenir.</span>
              {rateUpdatedAt && <span className="text-[10px] opacity-70">Son güncelleme: {new Date(rateUpdatedAt).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</span>}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Türkiye'den gelen müşterilere fiyatlar otomatik bu kurla TL'ye çevrilir. Nakit %10 indirim önerilir.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={refreshRate} data-testid="refresh-rate">
            <RefreshCw className="w-4 h-4 mr-2" /> Şimdi Yenile
          </Button>
        </CardContent>
      </Card>

      {/* Manual add/edit */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display">{form.id ? "Düzenle" : "Tek Tek Fiyat Ekle"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tedavi adı (TR)</Label>
              <Input data-testid="price-name-tr" placeholder="Ör: PicoSil Lazer" value={form.name_tr} onChange={(e) => setForm({ ...form, name_tr: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Vücut bölgesi (opsiyonel)</Label>
              <Input data-testid="price-region" placeholder="Ör: Yüz, Karın, Bacak" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Fiyat (EUR €)</Label>
              <Input data-testid="price-eur" type="number" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: parseFloat(e.target.value || 0) })} />
              {form.price_eur > 0 && rate && (
                <div className="text-xs text-muted-foreground mt-1">
                  ≈ {tryFor(form.price_eur).toLocaleString("tr-TR")} TL · nakit: {Math.round(tryFor(form.price_eur) * 0.9 / 50) * 50} TL
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Not (opsiyonel)</Label>
              <Input data-testid="price-note" placeholder="Ör: 3 seans dahil" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="price-save" onClick={save} style={{ background: "#B39B7A" }}>
              <Save className="w-4 h-4 mr-2" /> Kaydet
            </Button>
            {form.id && <Button variant="outline" onClick={() => setForm(empty)}>İptal</Button>}
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader><CardTitle className="font-display">Fiyat Listesi ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-8">Henüz fiyat yok — Excel ile toplu yükleyin veya tek tek ekleyin</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tedavi</TableHead>
                    <TableHead>Bölge</TableHead>
                    <TableHead className="text-right">EUR €</TableHead>
                    <TableHead className="text-right">TL ₺ (yaklaşık)</TableHead>
                    <TableHead className="text-right">Nakit (€/₺)</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((p) => {
                    const eur = Number(p.price_eur || 0);
                    const tl = tryFor(eur);
                    return (
                      <TableRow key={p.id} data-testid={`price-row-${p.id}`}>
                        <TableCell>
                          <div className="font-medium">{p.name_tr}</div>
                          {p.note && <div className="text-xs text-muted-foreground">{p.note}</div>}
                        </TableCell>
                        <TableCell>
                          {p.region ? <Badge variant="secondary" className="text-xs">{p.region}</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono">€{eur.toLocaleString("en-US")}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {tl ? `${tl.toLocaleString("tr-TR")} ₺` : "—"}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          <span style={{ color: "#8B9B88" }}>€{cashEur(eur)}</span>
                          {tl && <span className="block text-muted-foreground">{(Math.round(tl * 0.9 / 50) * 50).toLocaleString("tr-TR")} ₺</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => edit(p)} data-testid={`price-edit-${p.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => del(p.id)} className="text-destructive" data-testid={`price-del-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
